/**
 * #1670 — create_event. The pure half: validate, map, and say what happened.
 *
 * Until this tool there was NO path from anything a mom says to a row in
 * `events`, which is the table the whole Today tab renders from. To-dos,
 * groceries, reminders, Ready-to-Fire cards and family facts all had one. The
 * calendar did not, so "add Carly's birthday for Friday to my calendar"
 * reached a model with no way to do it.
 *
 * Everything here is deliberately free of a database client so it can be
 * tested directly. The write, the duplicate pre-check and the verified read
 * live in createEventWrite.ts.
 */

export const TITLE_MAX = 200;
export const LOCATION_MAX = 200;

/**
 * The three time states an `events` row can be in, named so the model must
 * pick one. There is no default and no omission — see `TIME_MODES` below.
 */
export type EventTimeMode = "timed" | "all_day" | "time_unknown";

export const TIME_MODES: readonly EventTimeMode[] = ["timed", "all_day", "time_unknown"];

/**
 * Which brain wrote the row — issue #1686, when voice gained create_event.
 *
 * `events` records provenance in source_type/source_table, and neither column
 * has a CHECK constraint (see summerPlanToTodayEvents.ts, which added
 * "summer_planner" the same way, and TodayTab's own far-future insert). So a
 * new value is additive and safe.
 *
 * ⚠️ Writing "kin_chat" for a voice event would have been one fewer parameter
 * and it is not worth it. Two reasons, and the second is the load-bearing one:
 *   1. It is false, and provenance is what someone reads at 11pm trying to work
 *      out where a row she does not recognise came from.
 *   2. Every voice write cap in this codebase is a Postgres trigger scoped to a
 *      column that says "this came from voice" (enforce_voice_todo_budget keys
 *      on `source = 'voice'`). `events` has no such trigger today — see the
 *      header of voiceCreateEvent.ts for why not, and #1694. If
 *      one is ever added, this column is the only thing it could key on, and a
 *      voice row indistinguishable from a chat row makes it unwritable.
 */
export type EventSource = "kin_chat" | "kin_voice";

export function validateTimeMode(raw: unknown): EventTimeMode | null {
  return typeof raw === "string" && (TIME_MODES as readonly string[]).includes(raw)
    ? (raw as EventTimeMode)
    : null;
}

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const HHMM_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

/**
 * A real calendar date, round-tripped.
 *
 * `Date.UTC` NORMALIZES an impossible date — 2026-02-30 silently becomes
 * March 2 — so the parse is only trustworthy if the result formats back to
 * the same string.
 */
export function validEventDate(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const m = YMD_RE.exec(raw.trim());
  if (!m) return null;
  const [, y, mo, d] = m;
  const instant = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  return instant.toISOString().slice(0, 10) === raw.trim() ? raw.trim() : null;
}

/**
 * `start_time` is a TEXT column and every reader does
 * `String(start_time).slice(0, 5)`, so a 12-hour value does not error — it
 * renders as garbage. `20260707150000_normalize_events_start_time_24hr.sql`
 * already cleaned this up once. Only 24-hour "HH:MM" / "HH:MM:SS" is accepted,
 * and it is always stored as "HH:MM:00".
 */
export function normalizeStartTime(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const m = HHMM_RE.exec(raw.trim());
  if (!m) return null;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (!Number.isInteger(hours) || hours < 0 || hours > 23) return null;
  if (!Number.isInteger(minutes) || minutes < 0 || minutes > 59) return null;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
}

/**
 * The `is_all_day` / `start_time` pair for a time mode.
 *
 * ⚠️ NEVER derive `is_all_day` as `!start_time`. Both `all_day` and
 * `time_unknown` have a null clock time and they are DIFFERENT states —
 * collapsing them destroys the only mechanism for recovering a missing time
 * and is bug #607, which five write sites made independently.
 * `src/lib/eventTimeSemantics.ts` holds the same contract for the app;
 * `createEventTimeSemantics.test.ts` asserts the two agree, because an Edge
 * Function cannot import from `src/`.
 */
export function timeColumnsForMode(
  mode: EventTimeMode,
  startTime: string | null,
): { is_all_day: boolean; start_time: string | null } {
  if (mode === "all_day") return { is_all_day: true, start_time: null };
  if (mode === "time_unknown") return { is_all_day: false, start_time: null };
  return { is_all_day: false, start_time: startTime };
}

// "YYYY-MM-DD" on HER wall clock. The edge runtime is UTC.
//
// Moved to ../_shared/localDate.ts in #1780 — kin-sms needs it for a confirmed
// series delete and cannot import from kin-chat. Re-exported here so every
// existing importer (createEventWrite, eventEditWrite, src/lib/voiceScheduleRead,
// createEventTool.test) keeps its path and there is still exactly ONE definition.
export { todayYmdInZone } from "../_shared/localDate.ts";

export interface CreateEventInput {
  title?: unknown;
  eventDate?: unknown;
  timeMode?: unknown;
  startTime?: unknown;
  location?: unknown;
  child?: unknown;
}

export interface EventRowFields {
  event_date: string;
  title: string;
  is_all_day: boolean;
  start_time: string | null;
  end_time: null;
  end_date: null;
  child: string | null;
  location: string | null;
  category: "event";
  source_type: EventSource;
  source_table: EventSource;
  source_id: null;
  status: "active";
  is_prep: false;
  is_starred: false;
  /**
   * #1694 — the id of the voice proposal this row came from, under a partial
   * unique index (user_id, propose_id). Absent for every other writer, and the
   * index is partial so those rows are not indexed at all.
   *
   * ⚠️ OPTIONAL, and the key is OMITTED rather than written as null when there is
   * no propose id. That is a deploy-order decision, not tidiness: this column does
   * not exist until Kasandra runs 20260924090000 by hand, and PostgREST fails an
   * insert that NAMES a column the table does not have (42703). Writing
   * `propose_id: null` on every row would mean chat's live create_event — the most
   * used write in the app — breaking the moment this code deployed and staying
   * broken until the SQL ran. Omitting it means chat's insert is byte-identical to
   * what it has always been, whatever order things happen in.
   */
  propose_id?: string;
}

export type BuildResult =
  | { ok: true; fields: EventRowFields; timeMode: EventTimeMode }
  | { ok: false; error: string };

function str(raw: unknown, max: number): string {
  return typeof raw === "string" ? raw.trim().slice(0, max) : "";
}

/**
 * Only a name that MATCHES one of her known kids, never a guess.
 *
 * For a user whose `known_kid_names` is empty, `dispatch-notifications` takes
 * the distinct `child` values off the day's rows and treats them AS her
 * children in the morning briefing. A guessed name there reads as Kin
 * hallucinating a child in a 7am text.
 */
export function matchKnownKid(raw: unknown, knownKidNames: readonly string[]): string | null {
  const wanted = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  if (!wanted) return null;
  for (const known of knownKidNames) {
    if (typeof known === "string" && known.trim().toLowerCase() === wanted) return known.trim();
  }
  return null;
}

export function buildEventRow(
  input: CreateEventInput,
  knownKidNames: readonly string[],
  // ⚠️ Defaulted so every existing caller — kin-chat's, and the #1670 tests —
  // is byte-identical without being touched. A required third argument here
  // would have been a change to a live chat write path in a PR about voice.
  source: EventSource = "kin_chat",
  // #1694 — minted by the voice capture gate when Kin PROPOSES the event, so the
  // id is fixed before she says yes and a duplicated call carries the same one.
  // Defaulted to null: chat has no propose step and writes the column as null.
  proposeId: string | null = null,
): BuildResult {
  const title = str(input.title, TITLE_MAX);
  if (!title) return { ok: false, error: "No title provided for the event." };

  const eventDate = validEventDate(input.eventDate);
  if (!eventDate) {
    return {
      ok: false,
      error:
        `event_date must be a real calendar date as YYYY-MM-DD. Got: ${String(input.eventDate)}`,
    };
  }

  const timeMode = validateTimeMode(input.timeMode);
  if (!timeMode) {
    return {
      ok: false,
      error:
        `time_mode is required and must be one of ${TIME_MODES.join(", ")}. ` +
        `If she gave a clock time use "timed"; if it runs all day (a birthday, a holiday, a closure) use "all_day"; ` +
        `use "time_unknown" ONLY if she has said she does not know the time yet. ` +
        `If you do not know, ASK her rather than retrying with a guess.`,
    };
  }

  let startTime: string | null = null;
  if (timeMode === "timed") {
    startTime = normalizeStartTime(input.startTime);
    if (!startTime) {
      return {
        ok: false,
        error:
          `time_mode "timed" needs start_time as a 24-hour "HH:MM". Got: ${String(input.startTime)}. ` +
          `Retry with a 24-hour time, or ask her what time it is.`,
      };
    }
  }

  return {
    ok: true,
    timeMode,
    fields: {
      event_date: eventDate,
      title,
      ...timeColumnsForMode(timeMode, startTime),
      end_time: null,
      end_date: null,
      child: matchKnownKid(input.child, knownKidNames),
      location: str(input.location, LOCATION_MAX) || null,
      // ⚠️ NON-NEGOTIABLE. inferCategory falls through to "task" for a row
      // with no clock time that is not all-day, so an event written without
      // this lands in her TO-DO LIST instead of her schedule.
      category: "event",
      source_type: source,
      source_table: source,
      source_id: null,
      status: "active",
      is_prep: false,
      is_starred: false,
      // Conditional spread, per the field's own comment: no key at all when there
      // is nothing to put in it.
      ...(proposeId ? { propose_id: proposeId } : {}),
    },
  };
}

/**
 * What the model is told after the row is written.
 *
 * The `time_unknown` branch is an HONESTY BACKSTOP. The enum is a steer, not a
 * gate — nothing can force the model to ask instead of picking
 * `time_unknown` — so what is enforced instead is that the reply cannot hide
 * it. The worst case becomes a visible gap she was told about, never a silent
 * pending state waiting on a question only the app ever asks.
 */
export function createEventResultText(fields: EventRowFields, timeMode: EventTimeMode): string {
  const whenPart = fields.is_all_day
    ? `${fields.event_date}, all day`
    : fields.start_time
      ? `${fields.event_date} at ${fields.start_time.slice(0, 5)}`
      : `${fields.event_date}`;
  const base = `Added "${fields.title}" to her calendar on ${whenPart}. It shows up on her Today tab that day.`;
  if (timeMode === "time_unknown") {
    return (
      `${base} ⚠️ It has NO TIME on it. You must say so in your reply — tell her the time is blank ` +
      `and she can set it on the event in the app, or tell you and you will fix it. Do NOT say it is all day, ` +
      `and do NOT let the reply imply the time is handled.`
    );
  }
  return `${base} Say plainly that it's on her calendar and when. Do not tell her to confirm anything.`;
}
